import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import {
  CATALOG_SEARCH_MIN_LENGTH,
  catalogHitLabel,
  findCatalogHits,
} from '@/pages/fulfillment/components/catalogSearch';
import type { CatalogHit } from '@/pages/fulfillment/components/catalogSearch';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';

interface AddLineItemModalProps {
  readonly show: boolean;
  readonly busy: boolean;
  readonly onHide: () => void;
  /** Resolves true when the line landed, which is what closes the dialog. */
  readonly onSubmit: (sku: string, quantity: number, requestedRoute: 'EXISTING_INVENTORY' | 'JIT') => Promise<boolean>;
}

/** Refusals name what is missing, so the disabled button is never silent about why. */
const describeBlock = (sku: string, quantity: string): string => {
  if (!sku.trim()) {
    return 'Enter a SKU and a quantity';
  }
  return Number.isInteger(Number(quantity)) && Number(quantity) > 0 ? '' : 'Enter a whole quantity above zero';
};

/** The typed text still goes out as the sku, so neither note may read as a refusal. */
const NO_CATALOG_MATCH = 'No product matches that SKU, brand or name — the SKU is sent as typed.';
const CATALOG_UNREACHABLE = 'The catalog could not be searched — the SKU is sent as typed.';

/**
 * Appending a line to an order [wireframe 8]. It lived as two loose inputs in the line-table footer,
 * where a form sat among buttons and the footer read as half toolbar, half form.
 */
export function AddLineItemModal({ show, busy, onHide, onSubmit }: AddLineItemModalProps) {
  const [sku, setSku] = useState('');
  const [quantity, setQuantity] = useState('');
  const [orderFromSupplier, setOrderFromSupplier] = useState(false);
  const [hits, setHits] = useState<CatalogHit[]>([]);
  /** The product the sku field was filled from, named back so a bare number is never added on trust. */
  const [picked, setPicked] = useState<CatalogHit | null>(null);
  const [catalogNote, setCatalogNote] = useState('');
  const searchAttemptRef = useRef(0);
  const quantityRef = useRef<HTMLInputElement>(null);

  // A dialog opened again is a new line, not a retry of the last one.
  useEffect(() => {
    searchAttemptRef.current += 1;
    if (show) {
      setSku('');
      setQuantity('');
      setOrderFromSupplier(false);
      setHits([]);
      setPicked(null);
      setCatalogNote('');
    }
  }, [show]);

  /**
   * The sku field is also the catalog search: an operator who knows only the brand or the name types
   * that and picks the product. The field stays free text, so a sku typed whole never needs the list.
   */
  const typeSku = async (typed: string) => {
    setSku(typed);
    setPicked(null);
    setCatalogNote('');
    setHits([]);
    // Lookups resolve out of order, so only the newest one may paint; an older answer is discarded.
    const attempt = (searchAttemptRef.current += 1);
    if (typed.trim().length < CATALOG_SEARCH_MIN_LENGTH) {
      setHits([]);
      return;
    }
    try {
      const found = await findCatalogHits(typed);
      if (attempt !== searchAttemptRef.current) {
        return;
      }
      setHits(found);
      setCatalogNote(found.length === 0 ? NO_CATALOG_MATCH : '');
    } catch {
      if (attempt !== searchAttemptRef.current) {
        return;
      }
      // A catalog that is down must not read as "nothing matched", and must not block a sku typed by hand.
      setHits([]);
      setCatalogNote(CATALOG_UNREACHABLE);
    }
  };

  const pickHit = (hit: CatalogHit) => {
    // A lookup still in flight would reopen the list under the choice just made.
    searchAttemptRef.current += 1;
    setSku(hit.sku);
    setPicked(hit);
    setHits([]);
    setCatalogNote('');
    quantityRef.current?.focus();
  };

  const block = describeBlock(sku, quantity);

  const submit = async () => {
    if (await onSubmit(sku.trim(), Number(quantity), orderFromSupplier ? 'JIT' : 'EXISTING_INVENTORY')) {
      onHide();
    }
  };

  return (
    <Modal {...ULTIMATE_MODAL} show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Add Line Item</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="add-order-sku">
          <Form.Label>SKU</Form.Label>
          <Form.Control
            value={sku}
            disabled={busy}
            autoComplete="off"
            placeholder="SKU No. · brand · product name"
            onChange={(event) => void typeSku(event.target.value)}
          />
          {picked && <div className="text-muted small mt-1">{catalogHitLabel(picked)}</div>}
          {catalogNote && <div className="text-muted small mt-1">{catalogNote}</div>}
          {hits.length > 0 && (
            <div className="border rounded mt-1" role="group" aria-label="Catalog suggestions">
              {hits.map((hit) => (
                <Button
                  key={hit.sku}
                  variant="link"
                  size="sm"
                  className="d-block text-start text-decoration-none w-100"
                  disabled={busy}
                  onClick={() => pickHit(hit)}
                >
                  {catalogHitLabel(hit)} <span className="text-muted">{hit.sku}</span>
                </Button>
              ))}
            </div>
          )}
        </Form.Group>
        <Form.Group controlId="add-order-quantity">
          <Form.Label>Quantity</Form.Label>
          <Form.Control
            ref={quantityRef}
            type="number"
            min={1}
            value={quantity}
            disabled={busy}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Form.Group>
        <Form.Check
          className="mt-3"
          id="add-order-line-jit"
          label="Order from supplier instead (JIT)"
          checked={orderFromSupplier}
          disabled={busy}
          onChange={(event) => setOrderFromSupplier(event.target.checked)}
        />
        <div className="text-muted small mt-1">
          By default, reserve available stock first; fall back to JIT if there is not enough free stock.
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="blocked-note">{busy ? '' : block}</span>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-line btn-sm" disabled={busy} onClick={onHide}>
            Cancel
          </button>
          <Button size="sm" disabled={busy || block !== ''} title={block} onClick={() => void submit()}>
            Add Line Item
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
